<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="8">
        <h1 class="title">{{ $t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.TITLE') }}</h1>
      </v-col>
    </v-row>

    <v-fab
      key="icon"
      color="light-grey"
      icon="mdi-close"
      class="pr-4"
      location="top end"
      size="48"
      absolute
      app
      appear
      @click="backToRepairFile"
    />

    <v-row v-if="pageLoaded">
      <v-col cols="3">
        <div class="ma-2 pb-5">
          <p class="font-weight-bold mb-3">
            {{ $t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.AVAILABILITY_STATUS_GROUP') }}
          </p>
          <v-checkbox
            density="compact"
            hide-details
            v-model="selectAllAvailabilityStatus"
            @click="switchToAllAvailabilityStatus()"
            :label="$t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.SELECT_ALL')"
          />
          <v-checkbox
            v-for="availabilityStatus in rentalProductAvailabilityStatusList"
            :key="availabilityStatus"
            density="compact"
            hide-details
            @change="selectAllAvailabilityStatus = false"
            v-model="selectedAvailabilityStatusList"
            :label="
              $t(`RENTAL_MODULE_LIST_PRODUCTS_PAGE.AVAILABILITY_STATUS.${availabilityStatus}`)
            "
            :value="availabilityStatus"
          />
          <v-divider />
        </div>
      </v-col>

      <v-col cols="9" style="height: 75vh">
        <!-- EMPTY LIST -->
        <v-row
          v-if="!loading && rentalProductList.length === 0"
          class="d-flex justify-center mt-4 pa-5"
        >
          <div class="text-md-center">
            {{ $t('COMMON.TABLE.NO_PRODUCT') }}
            <div class="d-flex align-center justify-center">
              <img src="@/assets/images/empty.svg" alt="Empty" class="empty-image" />
            </div>
          </div>
        </v-row>

        <v-infinite-scroll
          :height="'100%'"
          :items="rentalProductList"
          :onLoad="load"
          empty-text=""
          class="container-custom"
        >
          <template
            v-for="rentalProduct in rentalProductList"
            :key="rentalProduct.rental_technical_id"
          >
            <v-hover>
              <v-card
                :ripple="false"
                :id="rentalProduct.rental_technical_id"
                @click="showProductDetailed(rentalProduct)"
                class="product-card"
                :disabled="rentalProduct.rental_status !== AvailabilityStatusConstant.AVAILABLE"
              >
                <div>
                  <div class="picture">
                    <v-col v-if="rentalProduct.product_picture">
                      <v-img
                        class="picture-content"
                        :src="rentalProduct.product_picture"
                        alt="Product Preview"
                      />
                    </v-col>
                  </div>
                </div>

                <div class="info">
                  <div class="brand-label font-weight-black text-uppercase mt-2 mb-1">
                    {{ rentalProduct.product_manufacturer.name }}
                  </div>

                  <div class="product-name font-weight-black text-uppercase">
                    {{ rentalProduct.product_boomerang_name }}
                  </div>

                  <div
                    v-if="rentalProduct.rental_status === AvailabilityStatusConstant.AVAILABLE"
                    class="d-flex align-center mt-2 pa-0"
                  >
                    <span class="status-available-dot mr-2" />
                    <p class="product-status pr-2">
                      {{ $t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.AVAILABILITY_STATUS.AVAILABLE') }}
                    </p>
                  </div>

                  <div
                    v-else-if="rentalProduct.rental_status === AvailabilityStatusConstant.IN_COMING"
                    class="d-flex align-center mt-2 pa-0"
                  >
                    <span class="status-in-coming-dot mr-2" />
                    <p class="product-status pr-2">
                      {{ $t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.AVAILABILITY_STATUS.IN_COMING') }}
                    </p>
                  </div>

                  <div
                    v-else-if="
                      rentalProduct.rental_status === AvailabilityStatusConstant.UNAVAILABLE
                    "
                    class="d-flex align-center mt-2 pa-0"
                  >
                    <span class="status-unavailable-dot mr-2" />
                    <p class="product-status pr-2">
                      {{ $t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.AVAILABILITY_STATUS.UNAVAILABLE') }}
                    </p>
                  </div>

                  <div class="product-price font-weight-black mt-2 mb-2 text-subtitle-1">
                    {{ rentalProduct.rental_price.toFixed(2) }} {{ $t('COMMON.EURO') }}
                  </div>
                </div>
              </v-card>
            </v-hover>
          </template>
        </v-infinite-scroll>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  displayToast,
  capitalizeFirstLetter,
  shouldShowTooltip, handleResponseError
} from '@/components/utils/globalFunction'

import RentalProductService from '@/services/rentalModule/RentalProductService'
import RentalProductCategoryService from '@/services/rentalModule/RentalProductCategoryService'
import RentalProductAvailabilityStatusService from '@/services/rentalModule/RentalProductAvailabilityStatusService'
import AvailabilityStatusConstant from '@/constants/rentalModule/availabilityStatusConstant'
import RentalManufacturerService from '@/services/rentalModule/RentalManufacturerService'

export default {
  name: 'RentalProductListPage',
  computed: {
    AvailabilityStatusConstant() {
      return AvailabilityStatusConstant
    }
  },
  data() {
    return {
      loading: false,

      pageLoaded: false,

      page: 1,
      pages: null,

      repairFileBoomerangId: null,

      selectedAvailabilityStatusList: ['AVAILABLE'],
      selectAllAvailabilityStatus: false,

      manufacturerList: [],
      selectedManufacturer: null,

      selectedCategoryList: [],
      selectAllCategory: false,

      rentalProductCategoryList: [],
      rentalProductAvailabilityStatusList: [],
      rentalProductList: [],

      selectedRentalProduct: null,

      pageHeight: 0
    }
  },
  async created() {
    this.repairFileBoomerangId = this.$route.params.repairFileBoomerangId
    this.$store.commit('auth/setRentalRepairFileBoomerangId', this.repairFileBoomerangId)

    // NOTE: Disable manufacturer List and Category List filter.
    // Boomerang just need get all RentalProduct for now.
    // await this.getManufacturerList(this.repairFileBoomerangId)
    // await this.getDefaultManufacturer(this.repairFileBoomerangId)
    // await this.getRentalCategoryList()

    await this.getRentalAvailabilityStatusList()
    await this.getRentalProductPaginatedList()
    this.pageLoaded = true
  },
  watch: {
    // NOTE: Disable manufacturer List and Category List filter.
    // Boomerang just need get all RentalProduct for now.
    // async selectedManufacturer() {
    //   this.selectedCategoryList = []
    //   if (this.selectedManufacturer) {
    //     await this.getRentalCategoryList()
    //   }
    // },
    // async selectedCategoryList() {
    //   await this.getRentalProductPaginatedList()
    // },
    async selectedAvailabilityStatusList() {
      if (this.selectedAvailabilityStatusList.length === 0) {
        this.selectedAvailabilityStatusList = ['AVAILABLE']
      }
      await this.getRentalProductPaginatedList()
    }
  },
  methods: {
    shouldShowTooltip,
    capitalizeFirstLetter,
    async getRentalCategoryList() {
      this.loading = true
      try {
        this.rentalProductCategoryList =
          await RentalProductCategoryService.getRentalProductCategoryList(this.selectedManufacturer)
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async getRentalAvailabilityStatusList() {
      this.loading = true
      try {
        this.rentalProductAvailabilityStatusList =
          await RentalProductAvailabilityStatusService.getRentalProductAvailabilityStatusList()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async getRentalProductPaginatedList() {
      this.loading = true
      try {
        this.page = 1
        // NOTE: Disable manufacturer and Category List filter.
        // Boomerang just need get all RentalProduct for now.
        // const response = await RentalProductService.getRentalProductPaginatedList(
        //   this.page,
        //   this.selectedManufacturer,
        //   this.selectedCategoryList,
        //   this.selectedAvailabilityStatusList
        // )

        const response = await RentalProductService.getRentalProductPaginatedList(
          this.page,
          this.selectedAvailabilityStatusList
        )
        this.rentalProductList = response.data
        this.pages = response.max_page
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    showProductDetailed(rentalProduct) {
      if (rentalProduct.rental_status !== AvailabilityStatusConstant.AVAILABLE) {
        displayToast(this.$t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.AVAILABILITY_ERROR'), 'error')
      } else {
        this.$router.push('/rental-module/product-detail/' + rentalProduct.rental_technical_id)
      }
    },
    async load({ done }) {
      if (this.page === this.pages) {
        done('empty')
        return
      }
      setTimeout(async () => {
        done(await this.loadMore())
      }, 250)
    },
    async loadMore() {
      if (this.page < this.pages) {
        this.page = this.page + 1
        this.loading = true
        try {
          const response = await RentalProductService.getRentalProductPaginatedList(
            this.page,
            this.selectedCategoryList,
            this.selectedAvailabilityStatusList
          )
          this.rentalProductList.push(...response.data)
          return 'ok'
        } catch (error) {
          handleResponseError(error)
          return 'error'
        } finally {
          this.loading = false
        }
      }
    },
    backToRepairFile() {
      this.$store.commit('auth/resetRentalRepairFileBoomerangId')
      this.$router.push('/repair-file-detail/' + this.repairFileBoomerangId)
    },
    async getManufacturerList(repairFileBoomerangId) {
      try {
        const response = await RentalManufacturerService.getManufacturerListByRepairFileBoomerangId(
          repairFileBoomerangId
        )
        this.manufacturerList = response.item_list
      } catch (error) {
        handleResponseError(error)
      }
    },
    async getDefaultManufacturer(repairFileBoomerangId) {
      try {
        const response =
          await RentalManufacturerService.getDefaultManufacturerByRepairFileBoomerangId(
            repairFileBoomerangId
          )
        this.selectedManufacturer = response.technical_id
      } catch (error) {
        handleResponseError(error)
      }
    },
    switchToAllAvailabilityStatus() {
      if (this.selectAllAvailabilityStatus === true) {
        this.selectedAvailabilityStatusList = ['AVAILABLE']
      } else {
        this.selectedAvailabilityStatusList = this.rentalProductAvailabilityStatusList
      }
    }
  }
}
</script>

<style scoped>
.v-container {
  padding: 32px;
}

:deep(.v-infinite-scroll__side) {
  display: none;
  padding: unset;
}

.product-name {
  height: 48px;
  align-self: self-start;
}

.product-status {
  height: 24px;
  align-self: self-start;
}

.product-price {
  height: 24px;
  align-self: self-start;
}

.brand-label {
  align-self: self-start;
  color: #559a1b;
  height: 24px;
}

.product-card {
  box-shadow: unset;
  min-width: 250px !important;
  min-height: 350px !important;
  max-height: 350px !important;
  border: 1px solid #dadada;
  padding: 10px;
  border-radius: 10px;
  margin: 5px;
}

.picture .picture-content {
  width: 150px;
  height: 150px;
  margin: auto;
}

.info {
  margin: 0 10px;
}

.status-available-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #6eef54;
}

.status-in-coming-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #efbe54;
}

.status-unavailable-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ef5454;
}

.empty-image {
  min-width: 250px !important;
  max-width: 250px !important;
}

.title {
  font-size: 40px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

.container-custom {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
}
</style>
